import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { Chip, Spinner } from './ui';
import type { Language, MealChoice, MealChoices } from '../types';

const T = {
  en: {
    title: 'Choose a meal',
    search: 'Search…',
    favourites: 'Family favourites',
    regulars: 'Our regulars',
    others: 'Everything else',
    none: 'Nothing matches that.',
    noFavourites:
      'No favourites yet. Mark a meal with ♥ — on the week, in Meals, or after cooking it — and it will appear here first.',
    close: 'Cancel',
    choose: 'Choose',
    inTheHouse: 'Already bought',
    needs: (items: string[]) =>
      `Needs ${items.slice(0, 3).join(', ')}${items.length > 3 ? ` +${items.length - 3} more` : ''}`,
    min: 'min',
    quick: 'Quick',
    cooked: (n: number) => `cooked ${n}×`,
    never: 'never cooked',
    fish: 'Fish',
    loading: 'Finding what fits…',
  },
  fr: {
    title: 'Choisir un repas',
    search: 'Rechercher…',
    favourites: 'Favoris de la famille',
    regulars: 'Nos habitudes',
    others: 'Tout le reste',
    none: 'Aucun résultat.',
    noFavourites:
      'Pas encore de favoris. Marquez un plat d’un ♥ — dans la semaine, dans Repas, ou après l’avoir cuisiné — et il apparaîtra ici en premier.',
    close: 'Annuler',
    choose: 'Choisir',
    inTheHouse: 'Déjà acheté',
    needs: (items: string[]) =>
      `À acheter : ${items.slice(0, 3).join(', ')}${items.length > 3 ? ` +${items.length - 3}` : ''}`,
    min: 'min',
    quick: 'Rapide',
    cooked: (n: number) => `cuisiné ${n}×`,
    never: 'jamais cuisiné',
    fish: 'Poisson',
    loading: 'Recherche…',
  },
} as const;

/**
 * Picking a meal by hand (§34).
 *
 * The planner suggests; the family decides. Favourites come first because
 * "let's have one we like" is the most common reason to override a suggestion
 * in the first place.
 *
 * Everything offered here is already legal for the slot — right course, right
 * people, nobody's exclusions broken. What it does *not* hide is meals the
 * shopping has not covered: those stay choosable and say what they need, so
 * deciding to go out and buy something is a decision the family makes rather
 * than one the app makes for them.
 */
export function MealPicker({
  weekStart,
  assignmentKey,
  language,
  onChoose,
  onClose,
}: {
  weekStart: string;
  assignmentKey: string;
  language: Language;
  onChoose: (mealId: string) => void;
  onClose: () => void;
}) {
  const t = T[language];
  const [choices, setChoices] = useState<MealChoices | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    api
      .mealChoices(weekStart, assignmentKey, language)
      .then((result) => live && setChoices(result))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load'));
    return () => {
      live = false;
    };
  }, [weekStart, assignmentKey, language]);

  // Escape closes it, and the search box takes focus — this is a keyboard-first
  // thing on a laptop and a thumb-first thing on a phone.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    searchRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const groups = useMemo(() => {
    if (!choices) return [];
    const match = (meal: MealChoice) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (
        meal.name.toLowerCase().includes(q) || meal.description.toLowerCase().includes(q)
      );
    };
    return [
      { key: 'fav', label: t.favourites, meals: choices.favourites.filter(match) },
      { key: 'reg', label: t.regulars, meals: choices.regulars.filter(match) },
      { key: 'oth', label: t.others, meals: choices.others.filter(match) },
    ].filter((group) => group.meals.length > 0);
  }, [choices, query, t]);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
      >
        <div className="sheet-head">
          <h2>{t.title}</h2>
          <button className="btn" onClick={onClose}>
            {t.close}
          </button>
        </div>

        <input
          ref={searchRef}
          type="search"
          className="sheet-search"
          placeholder={t.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        <div className="sheet-body">
          {error && <p className="banner warn">{error}</p>}
          {!choices && !error && (
            <div style={{ display: 'grid', placeItems: 'center', padding: 40, gap: 12 }}>
              <Spinner />
              <span className="dim">{t.loading}</span>
            </div>
          )}

          {choices && choices.favourites.length === 0 && !query && (
            <p className="dim" style={{ padding: '0 0 14px' }}>
              {t.noFavourites}
            </p>
          )}

          {choices && groups.length === 0 && <p className="dim">{t.none}</p>}

          {groups.map((group) => (
            <section key={group.key}>
              <h3 className="sheet-group">{group.label}</h3>
              {group.meals.map((meal) => (
                <button
                  key={meal.id}
                  className="choice"
                  onClick={() => onChoose(meal.id)}
                  title={meal.description}
                >
                  <span className="choice-main">
                    <span className="choice-name">
                      {meal.isFavourite && <span aria-hidden="true">♥ </span>}
                      {meal.name}
                    </span>
                    {meal.description && <span className="choice-desc">{meal.description}</span>}
                    <span className="chips">
                      <Chip>
                        {meal.totalMinutes} {t.min}
                      </Chip>
                      {meal.prepType === 'quick' && <Chip>{t.quick}</Chip>}
                      {meal.isFish && <Chip kind="fish">{t.fish}</Chip>}
                      <Chip>{meal.timesCooked > 0 ? t.cooked(meal.timesCooked) : t.never}</Chip>
                    </span>
                  </span>

                  {/* Only meaningful once a shop has happened; before that
                      everything is equally "not bought yet". */}
                  {choices?.postShop && (
                    <span className={`choice-supply${meal.needs.length === 0 ? ' ok' : ''}`}>
                      {meal.needs.length === 0
                        ? `🛒 ${t.inTheHouse}`
                        : t.needs(meal.needs.map((n) => n.ingredient))}
                    </span>
                  )}
                </button>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
